import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type Client,
  type ClientListParams,
  cancelSubscription,
  getClientById,
  getClients,
  getPlanStatuses,
  getPlanTypes,
  removeClient,
  updateClient,
} from "@/apis/client/client-apis";
import { queryKeys } from "./keys";

export function useClients(params: ClientListParams) {
  return useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: () => getClients(params),
    placeholderData: keepPreviousData,
  });
}

export function useClient(id: number | null) {
  return useQuery({
    queryKey: queryKeys.clients.detail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getClientById(id ?? 0),
    enabled: Boolean(id),
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: Partial<Client> }) =>
      updateClient(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useRemoveClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => removeClient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cityId,
      userId,
      planId,
      endDate,
    }: {
      cityId: number;
      userId: number;
      planId: number;
      endDate?: string;
    }) => cancelSubscription(cityId, userId, planId, endDate),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.detail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function usePlanTypes() {
  return useQuery({
    queryKey: queryKeys.enums.planTypes,
    queryFn: getPlanTypes,
    staleTime: Infinity,
  });
}

export function usePlanStatuses() {
  return useQuery({
    queryKey: queryKeys.enums.planStatuses,
    queryFn: getPlanStatuses,
    staleTime: Infinity,
  });
}
